import type {
  DataSyncJob,
  UpdateDataSyncJobRequest,
} from './validations/types';

import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { queryKeysDataSyncJob } from './queryKeys';
import {
  parseDataSyncResponse,
  rawEntityResponseSchema,
} from './responseShapes';
import { updateDataSyncJobSchema } from './validations';

type UpdateJobParams = {
  jobId: string;
  data: UpdateDataSyncJobRequest;
};

/**
 * Updates an existing data sync job.
 */
export const updateDataSyncJob = async ({
  jobId,
  data,
}: UpdateJobParams): Promise<DataSyncJob> => {
  // Validate input
  const validatedData = updateDataSyncJobSchema.parse(data);

  const response = await vestigasApi
    .put(`data_sync/sync_job/${jobId}`, {
      json: validatedData,
    })
    .json<unknown>();

  return parseDataSyncResponse(
    rawEntityResponseSchema,
    response,
    'job update',
  ) as DataSyncJob;
};

/**
 * React Query mutation hook for updating a data sync job.
 * Automatically invalidates the jobs list query on success.
 *
 * @param options - Additional mutation options
 */
export const useMutationUpdateJob = (
  options?: Omit<
    UseMutationOptions<DataSyncJob, Error, UpdateJobParams>,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    ...options,
    mutationFn: updateDataSyncJob,
    async onSuccess() {
      // Invalidate both list and specific job detail
      await queryClient.invalidateQueries({
        queryKey: queryKeysDataSyncJob.base(),
      });

      ToastService.success('Sync-Job erfolgreich aktualisiert');
    },
  });

  return mutation;
};
